import axios from "axios";
import { ConfirmAlert } from "components";
import React, { useState, useEffect } from "react";
import { Card, Modal, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import EditReavaliacaoModal from "./editReavaliacaoModal";
import { IAvaliacao } from "./VisaoAnualGrid";

interface ReavaliacaoDetalheModalProps {
  onHide: () => void;
  show: boolean;
  status: "realizado" | "agendado" | "planejado" | 'em_andamento';
  paciente_id: number;
  paciente: string;
  especialidades: {
    id: number,
    descricao: string
  }[] | undefined
  id: number;
  instrumentos: any[];
  reRender: () => void;
  data: IAvaliacao
}

const deleteFn = async (id: number) => {
  await axios.delete(`/relatorio-parcial/${id}`);
};

const publicarFn = async (id: number) => {
  const response = await axios.post(`/relatorio-parcial/${id}/publicar`, {
    genPdf : false
  });
  return response.data;
};

export default function ReavaliacaoDetalheModal({
  onHide,
  show,
  status,
  paciente_id,
  paciente,
  id,
  especialidades,
  instrumentos,
  data,
  reRender,
}: ReavaliacaoDetalheModalProps) {
  const [deleting, setDeleting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [statusLocal, setStatusLocal] = useState<"realizado" | "agendado" | "indeterminado">(status);
  const [showEditModal, setShowEditModal] = useState(false)

  useEffect(() => {
    setStatusLocal(status);
  }, [status]);
const getInstrumentosString = (): string[] => {
  if (!instrumentos || instrumentos.length === 0) return ["-"];
  return instrumentos.map((i) => {
    const aplicadores = i.aplicadores

    const instrumento = i.instrumento;

    return `${instrumento}: ${aplicadores
      .map((a) => a?.trim())
      .join(", ")}`;
  });
};

  const handleDelete = async () => {
    setDeleting(true);
    setErro(null);
    try {
      await deleteFn(id);
      setDeleting(false);
      reRender();
      onHide();
    } catch (err) {
      const mensagem =
        (err as any)?.response?.data?.message ||
        (err as any)?.response?.data?.error ||
        "Erro ao deletar o relatório";
      setErro(mensagem);
      setDeleting(false);
    }finally{
      reRender()
    }
  };

  const handlePublicar = async () => {
    setPublishing(true);
    setErro(null);
    try {
      await publicarFn(id);
      // Atualizar status local imediatamente para "realizado"
      setStatusLocal("realizado");
      setPublishing(false);
      // Aguardar um pouco antes de reRender para permitir que a UI atualize
      setTimeout(() => {
        reRender();
      }, 500);
    } catch (err) {
      const mensagem =
        (err as any)?.response?.data?.message ||
        (err as any)?.response?.data?.error ||
        "Erro ao publicar a avaliação";
      setErro(mensagem);
      setPublishing(false);
    }
  };

  const statusConfig = {
    realizado: {
      label: "Realizada",
      backgroundColor: "#0aac7e",
      color: "#e6f9f3",
      icon: "mdi-check-circle",
    },
    agendado: {
      label: "Agendada",
      backgroundColor: "#f0ad4e",
      color: "#fef5ec",
      icon: "mdi-calendar-clock",
    },
    indeterminado : {
      label: "Não agendada",
      backgroundColor: "#9d9d9d",
      color: "#f4f4f4",
      icon: "mdi-calendar-clock",
    },
    planejado : {
      label: "Planejada",
      backgroundColor: "#49a4e1",
      color: "#49a4e142",
      icon: "mdi-calendar-clock",
    },
    em_andamento : {
      label: "Em andamento",
      backgroundColor: "#3bd6aaeb",
      color: "#3bd6aa3f",
      icon: "mdi-calendar-clock",
    }
  };

  const config = statusConfig[statusLocal] || statusConfig.agendado;

  return (
    <>
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton style={{ borderBottom: "1px solid #f0f0f0" }}>
        <Modal.Title style={{ fontSize: "1.1rem", fontWeight: 600, color: "#2c3e35" }}>
          {id ? `Avaliação nº${id}` : "Não agendada"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: "24px" }}>
        {erro && (
          <div
            style={{
              padding: "12px 16px",
              backgroundColor: "#fef5ec",
              borderRadius: "6px",
              color: "#c8960a",
              border: "1px solid #f5dda0",
              marginBottom: "16px",
              fontSize: "0.875rem",
            }}
          >
            <i className="mdi mdi-alert-circle me-2" />
            {erro}
          </div>
        )}

        <Card style={{ border: "1px solid #ebebeb", borderRadius: "8px" }}>
          <Card.Header
            style={{
              backgroundColor: "#f8f9fa",
              borderBottom: "1px solid #ebebeb",
              padding: "16px",
            }}
          >
            <div
              style={{
                fontSize: "0.875rem",
                color: "#999",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                fontWeight: 600,
                marginBottom: "8px",
              }}
            > 
              Paciente
            </div>
            <span
              style={{
                color: "#2c3e35",
                fontSize: "1rem",
                fontWeight: 500,
              }}
            >
              {paciente}
            </span>
          </Card.Header>

          <div style={{ padding: "16px" }}>
            <div style={{ 
              marginBottom: "16px", 
              display: 'flex',
              justifyContent: 'space-between'  
            }}>
              <div>
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "#999",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    fontWeight: 600,
                    marginBottom: "8px",
                  }}
                >
                  Status
                </div>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 12px",
                    backgroundColor: config.color,
                    border: `1px solid ${config.backgroundColor}20`,
                    borderRadius: "6px",
                  }}
                >
                  <i
                    className={`mdi ${config.icon}`}
                    style={{ color: config.backgroundColor, fontSize: "1rem" }}
                  />
                  <span style={{ color: config.backgroundColor, fontWeight: 500, fontSize: "0.875rem" }}>
                    {config.label}
                  </span>
                </div>                
              </div>
              <div>
                {especialidades?.length > 0 &&
                  <>
                    <div
                      style={{
                        fontSize: "0.75rem",
                        color: "#999",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        fontWeight: 600,
                        marginBottom: "8px",
                        textAlign: "right"
                      }}
                    >
                      Especialidades
                    </div>
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "8px 12px",
                        backgroundColor: config.color,
                        border: `1px solid ${config.backgroundColor}20`,
                        borderRadius: "6px",
                      }}
                    >
                        {especialidades?.map(e => {
                          return (
                            <span style={{ color: config.backgroundColor, fontWeight: 500, fontSize: "0.875rem" }}>
                              {e.descricao}
                            </span>
                          );
                        })}
                      </div>
                    </>                      
                }
              </div>
            </div>

            {instrumentos && instrumentos.length > 0 && (
              <div style={{ marginBottom: "16px" }}>
                <div
                  style={{
                    fontSize: "1rem",
                    color: "#999",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    fontWeight: 1000,
                    marginBottom: "8px"
                  }}
                ><span style={{
                  fontWeight : "bold"
                }}>
                  Instrumentos Utilizados
                </span>
                  
                </div>
                
                  {getInstrumentosString().map(i => {
                    return (
                      <div
                  style={{
                    margin : "8px",
                    backgroundColor: "#f8f9fa",
                    padding: "12px",
                    borderRadius: "6px",
                    border: "1px solid #ebebeb",
                    color: "#555",
                    fontSize: "0.875rem",
                    lineHeight: "1.6",
                  }}
                >{i}</div>
                    )
                  })}
              </div>
            )}
          </div>
        </Card>
      </Modal.Body>

      <Modal.Footer
        style={{
          borderTop: "1px solid #f0f0f0",
          padding: "16px 24px",
          gap: "8px",
          flexDirection: "row-reverse",
        }}
      >
        <Button
          variant="light"
          onClick={onHide}
          style={{ borderRadius: "6px", color: "#888" }}
        >
          Fechar
        </Button>

        <Link
          to={`/pacientes/ficha/${paciente_id}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "8px 16px",
            backgroundColor: "#3394c9",
            color: "#fff",
            borderRadius: "6px",
            textDecoration: "none",
            fontSize: "0.875rem",
            fontWeight: 500,
            border: "1px solid #3394c9",
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.backgroundColor = "#286db3";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.backgroundColor = "#3394c9";
          }}
        >
          <i className="mdi mdi-file-document-outline" />
          Ver Ficha do Paciente
        </Link>

        {(statusLocal !== "realizado" && statusLocal !== "indeterminado") && (
          <>
            <Button
              variant="success"
              onClick={handlePublicar}
              disabled={publishing}
              style={{ borderRadius: "6px" }}
            >
              {publishing ? (
                <>
                  <i className="mdi mdi-loading mdi-spin me-2" />
                  Publicando...
                </>
              ) : (
                <>
                  <i className="mdi mdi-check-circle me-2" />
                  Publicar Avaliação
                </>
              )}
            </Button>
            <Button
              variant="outline-primary"
              onClick={()=>{
                setShowEditModal(true)
                onHide()
              }}
            >
              Editar Avaliação
            </Button>   
          </>

        )}

        {statusLocal === "realizado" && (
          <ConfirmAlert
            text={`Tem certeza que deseja excluir a avaliação nº ${id}? Esta ação não pode ser desfeita.`}
            cb={handleDelete}
          >
            <Button
              variant="light"
              disabled={deleting}
              style={{
                borderRadius: "6px",
                color: "#d32f2f",
                cursor: deleting ? "not-allowed" : "pointer",
                opacity: deleting ? 0.6 : 1,
              }}
            >
              {deleting ? (
                <>
                  <i className="mdi mdi-loading mdi-spin me-2" />
                  Excluindo...
                </>
              ) : (
                <>
                  <i className="mdi mdi-delete me-2" />
                  Excluir
                </>
              )}
            </Button>
          </ConfirmAlert>
        )}
      </Modal.Footer>
    </Modal>
    <EditReavaliacaoModal 
        show={showEditModal} 
        onHide={()=>{
          setShowEditModal(false)
          reRender()
        }} 
        avaliacao={data}      
      />
      </>
  );
}